/**
 * ChangePasswordScreen — three use cases share this component:
 *
 *   1. Voluntary change from Profile → Security
 *   2. Forced change after admin-issued temp password
 *      (`must_change_password=true`; backend 423s every other route)
 *   3. Completion of a reset-token URL (?token=… present) — the
 *      current-password field is hidden; the token proves identity.
 *
 * Endpoints:
 *   POST /api/v1/auth/local/change-password { old_password, new_password }
 *   POST /api/v1/auth/local/reset           { token, new_password }
 *
 * Citadel Light theme — same look as SignInSplash/LocalSignInForm:
 * cream background (`var(--bg)`), white card with 0.5px border, navy
 * serif title with crimson period, mono uppercase eyebrow. Avoids the
 * darker UnlockScreen.css gradient which is only used in Electron.
 *
 * Debug instrumentation per latest/ENGINEERING-PRACTICES.md:
 *   - useDebugRegister exposes form state
 *   - debugLog.recordEvent on every meaningful transition
 *   - data-debug-id on every interactive element
 */

import { useEffect, useState } from 'react';
import { PRODUCT_NAME } from '@cipherops/data-source';
import { useDebugRegister, debugLog } from '../lib/debug';

interface Props {
  /** Heads-up banner is shown for the forced-change-after-temp-password flow. */
  forceChange?: boolean;
  /** Reset token from the URL. When present, hides the current-password
   *  field and posts to /reset. */
  resetToken?: string;
  /** Where to navigate after successful change. Defaults to '/'. */
  onSuccessRedirect?: string;
}

function strengthHint(pw: string): string {
  if (!pw) return '';
  if (pw.length < 12) return 'Too short — 12 characters minimum.';
  const variety =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^a-zA-Z0-9]/.test(pw) ? 1 : 0);
  return variety < 3 ? 'OK — try mixing letters, digits, symbols.' : 'Strong.';
}

export function ChangePasswordScreen({ forceChange, resetToken, onSuccessRedirect }: Props) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // Mode discriminator for both the debug panel and the title/copy.
  const mode: 'voluntary' | 'force-change' | 'reset-token' = resetToken
    ? 'reset-token'
    : forceChange
      ? 'force-change'
      : 'voluntary';

  useDebugRegister('ChangePasswordScreen', {
    mode,
    has_old_password: oldPassword.length > 0,
    has_new_password: newPassword.length > 0,
    has_confirm: confirmPassword.length > 0,
    passwords_match: newPassword === confirmPassword && newPassword.length > 0,
    new_password_len: newPassword.length,
    submitting,
    ok,
    error,
  });

  useEffect(() => {
    document.title = forceChange
      ? `Change your password · ${PRODUCT_NAME}`
      : `Change password · ${PRODUCT_NAME}`;
    debugLog.recordEvent({
      type: 'change_password_mounted',
      description: `ChangePasswordScreen mounted in ${mode} mode`,
      data: { mode, has_token: !!resetToken },
    });
  }, [forceChange, mode, resetToken]);

  const canSubmit =
    newPassword.length >= 12 &&
    newPassword === confirmPassword &&
    (resetToken !== undefined || oldPassword.length > 0) &&
    !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const endpoint = resetToken
      ? '/api/v1/auth/local/reset'
      : '/api/v1/auth/local/change-password';
    debugLog.recordEvent({
      type: 'change_password_submitted',
      description: `submitting password change via ${endpoint}`,
      data: { mode, endpoint },
    });
    try {
      const body: Record<string, string> = { new_password: newPassword };
      if (resetToken) body.token = resetToken;
      else body.old_password = oldPassword;

      const startedAt = performance.now();
      const r = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const durationMs = performance.now() - startedAt;
      debugLog.recordApiCall({
        channel: endpoint,
        path: endpoint,
        payload: { ...body, ...(body.new_password ? { new_password: '[REDACTED]' } : {}), ...(body.old_password ? { old_password: '[REDACTED]' } : {}) },
        ok: r.ok,
        status: r.status,
        durationMs,
      });

      if (!r.ok) {
        const j = await r.json().catch(() => null);
        const message =
          j?.message ??
          (r.status === 401
            ? 'Current password is incorrect.'
            : 'Password change failed.');
        debugLog.recordEvent({
          type: 'change_password_failed',
          description: `${j?.code ?? `HTTP_${r.status}`}: ${message}`,
          data: { mode, code: j?.code, status: r.status },
        });
        setError(message);
        setSubmitting(false);
        return;
      }
      debugLog.recordEvent({
        type: 'change_password_succeeded',
        description: `password ${mode === 'voluntary' ? 'changed' : 'set'} successfully`,
        data: { mode },
      });
      setOk(true);
      window.setTimeout(() => {
        window.location.href = onSuccessRedirect ?? '/';
      }, 900);
    } catch (err) {
      debugLog.recordEvent({
        type: 'change_password_network_error',
        description: 'fetch failed before response',
        data: { mode, error: err instanceof Error ? err.message : String(err) },
      });
      setError('Could not reach the server. Try again.');
      setSubmitting(false);
    }
  }

  const title = forceChange ? 'Set a new password' : resetToken ? 'Choose a new password' : 'Change password';
  const subtitle = forceChange
    ? 'Your administrator issued you a temporary password — pick your own now.'
    : resetToken
      ? 'Enter a new password to complete the reset.'
      : `Update the password you use to sign in to ${PRODUCT_NAME}.`;
  const buttonLabel = submitting
    ? 'Saving…'
    : forceChange
      ? 'Set password and continue'
      : 'Change password';

  return (
    <main style={pageStyle} data-debug-id={`change-password-${mode}`}>
      <section style={cardStyle}>
        <div style={eyebrowStyle}>
          {forceChange ? 'Action required' : resetToken ? 'Password reset' : 'Account security'}
        </div>
        <h1 style={titleStyle}>
          {PRODUCT_NAME}<span style={{ color: 'var(--sev-high-label, #c44a1a)' }}>.</span>
        </h1>
        <p style={subtitleStyle}>{subtitle}</p>

        <form onSubmit={handleSubmit} style={formStyle}>
          <h2 style={sectionHeadStyle}>{title}</h2>

          {!resetToken && (
            <label style={labelStyle}>
              <span>Current password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                disabled={submitting}
                data-debug-id="old-password-input"
                required
                style={inputStyle}
              />
            </label>
          )}

          <label style={labelStyle}>
            <span>New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 12 characters"
              disabled={submitting}
              data-debug-id="new-password-input"
              required
              minLength={12}
              style={inputStyle}
            />
            {newPassword && (
              <small style={hintStyle}>{strengthHint(newPassword)}</small>
            )}
          </label>

          <label style={labelStyle}>
            <span>Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitting}
              data-debug-id="confirm-password-input"
              required
              minLength={12}
              style={inputStyle}
            />
            {confirmPassword && confirmPassword !== newPassword && (
              <small style={{ ...hintStyle, color: 'var(--sev-high-label, #c44a1a)' }}>
                Passwords don't match.
              </small>
            )}
          </label>

          {error && (
            <div style={errorStyle} data-debug-id="change-password-error">
              {error}
            </div>
          )}
          {ok && (
            <div style={successStyle} data-debug-id="change-password-success">
              Password changed. Redirecting…
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            data-debug-id="change-password-submit"
            style={canSubmit ? submitButtonStyle : submitButtonDisabledStyle}
          >
            {buttonLabel}
          </button>
        </form>

        <p style={footerStyle}>
          Press <kbd style={kbdStyle}>Ctrl+Shift+D</kbd> at any time to open the Debug Panel.
        </p>
      </section>
    </main>
  );
}

// ── Citadel Light cream theme styles (shared with SignInSplash/LocalSignInForm) ─

const pageStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '24px',
  fontFamily: 'var(--sans, system-ui, sans-serif)',
  background: 'var(--bg, #f6f4f0)',
};

const cardStyle: React.CSSProperties = {
  maxWidth: 460,
  width: '100%',
  background: 'var(--surface, #ffffff)',
  border: '0.5px solid var(--border-light, #e0ddd8)',
  padding: '40px 36px',
};

const eyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--mono, "Courier New", monospace)',
  fontSize: 10,
  letterSpacing: 3,
  textTransform: 'uppercase',
  color: 'var(--text-muted, #7a7d8a)',
  marginBottom: 8,
  textAlign: 'center',
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--serif, Georgia, serif)',
  fontSize: 28,
  fontWeight: 'normal',
  margin: '0 0 8px',
  textAlign: 'center',
};

const subtitleStyle: React.CSSProperties = {
  color: 'var(--text-muted, #7a7d8a)',
  fontSize: 13,
  lineHeight: 1.7,
  margin: '0 0 24px',
  textAlign: 'center',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  borderTop: '1.5px solid var(--accent, #8b1a2e)',
  paddingTop: 20,
};

const sectionHeadStyle: React.CSSProperties = {
  fontFamily: 'var(--serif, Georgia, serif)',
  fontSize: 18,
  fontWeight: 'normal',
  margin: '0 0 4px',
  textAlign: 'center',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 11,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: 'var(--text-muted, #7a7d8a)',
  fontFamily: 'var(--mono, "Courier New", monospace)',
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'var(--sans, system-ui, sans-serif)',
  border: '0.5px solid var(--border, #d0ccc4)',
  borderRadius: 0,
  background: 'var(--surface, #ffffff)',
  color: 'var(--text, #1a1f2e)',
  textTransform: 'none',
  letterSpacing: 0,
};

const hintStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted, #7a7d8a)',
  fontFamily: 'var(--sans, system-ui, sans-serif)',
  textTransform: 'none',
  letterSpacing: 0,
  marginTop: 2,
};

const errorStyle: React.CSSProperties = {
  background: 'rgba(196, 74, 26, 0.08)',
  borderLeft: '3px solid var(--sev-high-border, #c44a1a)',
  color: 'var(--sev-high-label, #c44a1a)',
  padding: '10px 12px',
  fontSize: 12,
  lineHeight: 1.5,
};

const successStyle: React.CSSProperties = {
  background: 'rgba(45, 122, 66, 0.08)',
  borderLeft: '3px solid #2d7a42',
  color: '#1e6635',
  padding: '10px 12px',
  fontSize: 12,
  lineHeight: 1.5,
};

const submitButtonStyle: React.CSSProperties = {
  padding: '12px 24px',
  background: 'var(--brand, #1a1f2e)',
  color: '#ffffff',
  fontSize: 14,
  letterSpacing: 0.3,
  border: 'none',
  borderRadius: 0,
  cursor: 'pointer',
  marginTop: 6,
};

const submitButtonDisabledStyle: React.CSSProperties = {
  ...submitButtonStyle,
  background: 'var(--text-muted, #7a7d8a)',
  cursor: 'not-allowed',
};

const footerStyle: React.CSSProperties = {
  marginTop: 24,
  fontSize: 11,
  color: 'var(--text-muted, #7a7d8a)',
  lineHeight: 1.6,
  paddingTop: 14,
  borderTop: '1px solid var(--border-light, #e0ddd8)',
  textAlign: 'center',
};

const kbdStyle: React.CSSProperties = {
  fontFamily: 'var(--mono, "Courier New", monospace)',
  fontSize: 10,
  background: 'var(--code-bg, #f0ede8)',
  border: '0.5px solid var(--border, #d0ccc4)',
  padding: '1px 5px',
  letterSpacing: 0.5,
};
